import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useMediaQuery, useTheme } from "@mui/material";
import { apiClientRaw } from "../../../api/httpClient";
import { setSessionAuthenticated } from "../../../globals/utils/authSession";
import { setAuthData } from "../../../store/slices/authSlice";
import { setPgId } from "../../../store/slices/pgSlice";
import { errorHandler } from "../../../globals/utils/error-handler";

export function useLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotUsername, setForgotUsername] = useState(false);
  const [usernameOtpSent, setUsernameOtpSent] = useState(false);
  const [retrievedUsername, setRetrievedUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  // 2FA State
  const [needs2FA, setNeeds2FA] = useState(false);
  const [userID, setUserID] = useState("");
  const [otp2FA, setOtp2FA] = useState("");
  const roleRef = useRef(null);

  // Error states for inline validation
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otp2FAError, setOtp2FAError] = useState("");

  // State for Multiple Accounts Selection
  const [accountsList, setAccountsList] = useState([]);
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loginAccountsList, setLoginAccountsList] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const primaryColor = "#333333";
  const secondaryColor = "#666666";

  useEffect(() => {
    const lastVisitedPath = localStorage.getItem("lastVisitedPath");
    const hasAuth = localStorage.getItem("isAuthenticated");
    if (lastVisitedPath && hasAuth) {
      navigate(lastVisitedPath);
    }
  }, [navigate]);

  useEffect(() => {
    try {
      const prefill = sessionStorage.getItem("prefillEmail");
      if (prefill) {
        setEmail(prefill);
        sessionStorage.removeItem("prefillEmail");
      }
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.get("action") === "forgot-username") {
          setForgotUsername(true);
        }
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, []);

  const checkLoginAccountsByEmail = async (value) => {
    if (!value?.includes("@")) {
      setLoginAccountsList([]);
      return;
    }
    try {
      const resp = await apiClientRaw("/accounts-by-email", "POST", { email: value.trim() });
      if (resp.status === 200) {
        const accounts = Array.isArray(resp.data?.accounts) ? resp.data.accounts : [];
        setLoginAccountsList(accounts.length > 1 ? accounts : []);
      } else {
        setLoginAccountsList([]);
      }
    } catch {
      setLoginAccountsList([]);
    }
  };

  const handleLogin = async () => {
    if (!validateEmail() || !validatePassword()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/login`, "POST", {
        username: email,
        password,
      });

      if (response.status === 202) {
        // 2FA Required
        setNeeds2FA(true);
        setUserID(response.data.user_id);
        toast.info("OTP sent to your registered email");
        return;
      }

      if (response.status === 200) {
        setLoginAccountsList([]);
        handleLoginSuccess(response.data);
      } else {
        const message = response.data?.detail
          || response.data?.error
          || "Login failed. Please check your credentials and try again.";
        toast.error(message);
      }
    } catch (error) {
      if (error.response?.status === 202) {
        setNeeds2FA(true);
        setUserID(error.response.data.user_id);
        toast.info("OTP sent to your registered email");
      } else {
        console.error("Login error:", error);
        errorHandler(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async () => {
    if (!validateOtp2FA()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/2fa-verify`, "POST", {
        user_id: userID,
        otp: otp2FA,
        password: password,
      });

      if (response.status === 200) {
        setLoginAccountsList([]);
        handleLoginSuccess(response.data);
      } else {
        toast.error(response.data?.detail || "Verification failed. Please try again.");
      }
    } catch (error) {
      console.error(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async (data) => {
    setSessionAuthenticated();
    dispatch(
      setAuthData({
        user: data.user,
        phoneNumber: data.phonenumber,
        role: data.role,
        role_id: data.role_id,
        pg_id: data.pg_id,
      })
    );
    dispatch(setPgId(data.pg_id));
    toast.success("Login successful! Redirecting...");
    roleRef.current = data.role;
    const rolePathMap = {
      super_user: "/admin_home",
      internal_user: "/internal_user_home",
      law_firm_user: "/law_firm_user_home",
      law_firm_admin: "/law_firm_admin_home",
      carrier_team_member: "/carrier_team_member_dashboard",
      pg_contact: "/pg_contact_home",
    };
    navigate(rolePathMap[data.role] || "/professional_guardian_home");
  };

  const handleForgotPassword = () => {
    setForgotPassword(true);
    setForgotUsername(false);
    setEmail(""); // Clear email so user can type it fresh
    setEmailError("");
  };

  const handleForgotUsernameLink = () => {
    setForgotUsername(true);
    setForgotPassword(false);
    setLoginAccountsList([]);
    setEmail("");
    setEmailError("");
  };

  const handleBackToLogin = () => {
    setForgotPassword(false);
    setForgotUsername(false);
    setLoginAccountsList([]);
    setOtpSent(false);
    setUsernameOtpSent(false);
    setRetrievedUsername("");
    setNewPassword("");
    setConfirmPassword("");
    setOtp("");
    setEmail("");
    setEmailError("");
    setOtpError("");
  };

  const handleCreateAccount = () => {
    navigate("/first-time-register");
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setEmailError("Username is required");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = () => {
    if (!password.trim()) {
      setPasswordError("Password is required");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateNewPassword = () => {
    if (!newPassword.trim()) {
      setNewPasswordError("New password is required");
      return false;
    }
    if (newPassword.length < 8) {
      setNewPasswordError("Password must be at least 8 characters");
      return false;
    }
    if (!/\d/.test(newPassword)) {
      setNewPasswordError("Password must contain at least one number");
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setNewPasswordError(
        "Password must contain at least one special character"
      );
      return false;
    }
    setNewPasswordError("");
    return true;
  };

  const validateConfirmPassword = () => {
    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Please confirm your password");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  const validateOtp = () => {
    if (!otp.trim()) {
      setOtpError("OTP is required");
      return false;
    }
    setOtpError("");
    return true;
  };

  const validateOtp2FA = () => {
    if (!otp2FA.trim()) {
      setOtp2FAError("Verification code is required");
      return false;
    }
    setOtp2FAError("");
    return true;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (forgotUsername) {
        if (retrievedUsername) {
          handleBackToLogin();
        } else if (usernameOtpSent) {
          handleVerifyUsernameOTP();
        } else {
          handleSendUsernameOTP();
        }
      } else if (forgotPassword) {
        if (otpSent) {
          handleResetPassword();
        } else {
          handleOnclickSendOTP();
        }
      } else if (needs2FA) {
        handle2FAVerification();
      } else {
        handleLogin();
      }
    }
  };

  const handleOnclickSendOTP = async () => {
    if (!validateEmail()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/forgot-password`, "POST", {
        username: email,
      });
      if (response) setOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (error) {
      console.error(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendUsernameOTP = async (specificUserId = null) => {
    if (!validateEmail()) return;

    // Sanitize input: if called via onClick, specificUserId might be an Event object
    const actualUserId =
      typeof specificUserId === "number" || typeof specificUserId === "string"
        ? specificUserId
        : null;

    setLoading(true);
    try {
      const payload = { email: email };
      if (actualUserId) {
        payload.user_id = actualUserId;
      }

      const response = await apiClientRaw(`/forgot-username`, "POST", payload);

      if (response.status === 200) {
        if (response.data.multiple_accounts) {
          // Multiple accounts found, show selection UI
          setAccountsList(response.data.accounts);
          setShowAccountSelection(true);
        } else {
          // OTP sent to single account
          setUsernameOtpSent(true);
          // Store the user_id returned by backend for the verify step
          if (response.data.user_id) {
            setSelectedUserId(response.data.user_id);
          } else if (actualUserId) {
            setSelectedUserId(actualUserId);
          }
          toast.success("OTP sent to your email!");
          setShowAccountSelection(false); // Close dialog if open
        }
      } else {
        toast.error(response.data?.detail || "Failed to send OTP.");
      }
    } catch (error) {
      console.error(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (userId) => {
    setSelectedUserId(userId);
    handleSendUsernameOTP(userId);
  };

  const pickAccountRecoverUsername = (account) => {
    setForgotUsername(true);
    setLoginAccountsList([]);
    handleSendUsernameOTP(account.user_id);
  };

  const handleVerifyUsernameOTP = async () => {
    if (!validateOtp()) return;
    setLoading(true);
    try {
      // Endpoint from user screenshots/plan
      const response = await apiClientRaw(`/forgot-username-verify`, "POST", {
        email: email,
        otp: otp,
        user_id: selectedUserId, // Include the selected user_id
      });
      if (response.status === 200) {
        setRetrievedUsername(response.data.username);
        toast.success("Username retrieved successfully!");
      } else {
        toast.error(response.data?.detail || "Invalid OTP or Email.");
      }
    } catch (error) {
      console.error(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (
      !validateNewPassword() ||
      !validateConfirmPassword() ||
      !validateOtp()
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiClientRaw(`/reset-password`, "POST", {
        username: email,
        password: newPassword,
        otp,
      });
      if (response.status === 200) {
        toast.success("Password reset successful!");
        setForgotPassword(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return {
    showPassword,
    setShowPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    loading,
    setLoading,
    email,
    setEmail,
    password,
    setPassword,
    forgotPassword,
    setForgotPassword,
    forgotUsername,
    setForgotUsername,
    usernameOtpSent,
    setUsernameOtpSent,
    retrievedUsername,
    setRetrievedUsername,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    otp,
    setOtp,
    otpSent,
    setOtpSent,
    openCalculator,
    setOpenCalculator,
    openSupport,
    setOpenSupport,
    rememberMe,
    setRememberMe,
    needs2FA,
    setNeeds2FA,
    userID,
    setUserID,
    otp2FA,
    setOtp2FA,
    roleRef,
    emailError,
    setEmailError,
    passwordError,
    setPasswordError,
    newPasswordError,
    setNewPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,
    otpError,
    setOtpError,
    otp2FAError,
    setOtp2FAError,
    accountsList,
    setAccountsList,
    showAccountSelection,
    setShowAccountSelection,
    selectedUserId,
    setSelectedUserId,
    loginAccountsList,
    checkLoginAccountsByEmail,
    pickAccountRecoverUsername,
    navigate,
    isMobile,
    primaryColor,
    secondaryColor,
    handleLogin,
    handle2FAVerification,
    handleForgotPassword,
    handleForgotUsernameLink,
    handleBackToLogin,
    handleCreateAccount,
    validateEmail,
    validatePassword,
    validateNewPassword,
    validateConfirmPassword,
    validateOtp,
    validateOtp2FA,
    handleKeyDown,
    handleOnclickSendOTP,
    handleSendUsernameOTP,
    handleAccountSelect,
    handleVerifyUsernameOTP,
    handleResetPassword,
  };
}

export default useLogin;
